import { Injectable } from "@angular/core";
import { ItemWithRelations, LocationWithRelations } from "src/app/api/models";

@Injectable({
  providedIn: "root",
})
export class LocationService {
  constructor() {}

    /**
   * Gets the location string for the given item.
   *
   * @param {ItemWithRelations} item - The item for which to get the location string.
   * @param {LocationWithRelations[]} locations - The list of locations to use for getting the location label.
   * @returns {{ label: string; tooltip: string | undefined }} - An object containing the location label and tooltip.
   */
  getLocationString(
    item: ItemWithRelations,
    locations?: LocationWithRelations[]
  ): { label: string; tooltip: string | undefined } {
    // Initialize the location object with empty label and tooltip
    let location = { label: "", tooltip: "" };

    // Check if the item has a location
    if (item.location && locations) {
      // Get the location label using the getLocationLabel method
      location.label = this.getLocationLabel(item.location, locations);
      // Set the tooltip to the location description if it exists, otherwise set it to an empty string
      location.tooltip = item.location.description ? item.location.description : "";
    }

    // Return the location object
    return location;
  }

  /**
   * Recursively generates a label for a location, including its parent locations.
   * @param location The location for which to generate the label.
   * @param locations The list of all locations to find parent locations.
   * @returns The generated location label.
   */
  getLocationLabel(
    location: LocationWithRelations,
    locations: LocationWithRelations[]
  ): string {
    let label = "";
    if (!location) {
      return "";
    }

    // If the location has a parent, recursively get the parent's label and append the current location's title
    if (location.parentId) {
      const parent = locations.find((loc) => loc.id === location.parentId);
      return `${this.getLocationLabel(parent, locations)} > ${location.title}`;
    } else {
      // If the location has no parent, return its title
      return `${location.title}${label}`;
    }
  }

  /**
   * Flattens a hierarchical list of locations into a single-level list.
   * @param inventoryLocations The hierarchical list of locations.
   * @returns The flattened list of locations.
   */
  flattenLocations(
    inventoryLocations: LocationWithRelations[]
  ): LocationWithRelations[] {
    const locations = [];
    inventoryLocations?.forEach((location) => {
      // Add the current location to the flattened list
      locations.push(location);
      // If the current location has children, recursively flatten them and add to the list
      if (location.children) {
        locations.push(...this.flattenLocations(location.children));
      }
    });
    return locations;
  }
}
